import { useCallback, useEffect, useState } from "react";
import { listAlbums } from "api";
import { Album } from "model/albumModel";
import { BaseTask } from "model/activitiesModel";
import useSelectedOrganization from "./useSelectedOrganization";

/**
 * Album del task corrente. Si ricarica quando cambia il task.
 *
 * `enabled` è false quando il task non ha allegati-immagine: un album è fatto
 * solo di immagini-allegato, quindi in quel caso non si chiama nemmeno il
 * backend. Se la lettura fallisce (task non visibile, backend non deployato) si
 * mostrano semplicemente zero album.
 */
const useAlbums = (
  task: BaseTask | null,
  enabled: boolean = true
): [
    Album[],
    () => Promise<void>,
    boolean,
    React.Dispatch<React.SetStateAction<Album[]>>
  ] => {
  const organization = useSelectedOrganization();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(false);

  const idprocess = (task as { idprocess?: number } | null)?.idprocess;

  const getAlbums = useCallback(async () => {
    if (!idprocess || !organization?.id || !enabled) {
      setAlbums([]);
      return;
    }
    setLoading(true);
    try {
      setAlbums(await listAlbums(idprocess, organization.id));
    } catch {
      setAlbums([]);
    } finally {
      setLoading(false);
    }
  }, [idprocess, organization?.id, enabled]);

  useEffect(() => {
    setAlbums([]);
    if (enabled) getAlbums();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idprocess, organization?.id, enabled]);

  return [albums, getAlbums, loading, setAlbums];
};

export default useAlbums;
